import { type RouteRecordRaw } from '@bv/router';
import { useBrandStore } from '@bv/stores/brand';
import { useUserStore } from '@bv/stores/user';
import { generateBreadcrumbs } from '@bv/utils/route/breadcrumbs/generate-breadcrumbs';
import { aiRoutesGenerator } from '@bvai/router';
import { defineAsyncComponent, computed } from 'vue';

import { useQueuesDataStore } from '@/stores/reports/home.store';

import type { RouteLocationNormalized, NavigationGuardNext } from 'vue-router';


const aiBreadcrumbs = generateBreadcrumbs({
  label: 'ai.ai',
  route: { name: 'ai' },
});

const loadData = async (
  to: RouteLocationNormalized,
  from: RouteLocationNormalized,
  next: NavigationGuardNext,
): Promise<void> => {
  await useQueuesDataStore().getQueuesData();
  next();
};

const voipAiRoutes: RouteRecordRaw = {
  path: '/ai',
  name: 'ai',
  component: defineAsyncComponent(() => import('@/views/AiView.vue')),
  meta: {
    redirectToFirstAccessibleChild: true,
    canAccess: () => !!useUserStore().callCenterService?.settings.ai_service?.enabled,
    redirectUnauthorized: { name: 'dashboard' },
    breadcrumbs: aiBreadcrumbs(),
    header: {
      title: computed(() => (useBrandStore().config?.isDefault ? 'ai.bevatel_ai' : 'ai.ai')),
    },
    sidebar: [
      {
        title: 'ai.nav.ai_analysis',
        items: [
          {
            title: 'ai.nav.dashboard',
            route: { name: 'ai-dashboard' },
            icon: 'bvi-dashboard',
            permissions: 'read-ai-dashboard',
          },
          {
            title: 'ai.nav.analysis_report',
            route: { name: 'ai-report' },
            icon: 'bvi-chart-line',
            permissions: 'read-ai-analysis-report',
          },
          {
            title: 'ai.nav.analysis_settings',
            route: { name: 'ai-settings' },
            icon: 'bvi-controls',
            permissions: 'read-ai-analysis-settings',
          },
        ],
      },
      {
        title: 'ai.nav.ai_agent',
        badge: { text: 'reports.nav.beta', color: 'var(--success-400)' },
        isOpened: true,
        items: [
          {
            title: 'ai.nav.agent_performance',
            route: { name: 'ai-performance-report' },
            icon: 'bvi-dashboard',
            permissions: 'read-ai-performance-report',
          },
          {
            title: 'ai.nav.my_agents',
            route: { name: 'ai-agents-list' },
            icon: 'bvi-ai-solid',
            permissions: 'read-ai-agents',
          },
          {
            title: 'ai.nav.voices',
            route: { name: 'ai-voices-list' },
            icon: 'bvi-voice-circle',
            permissions: 'read-ai-voices',
          },
          {
            title: 'ai.nav.knowledge_base',
            route: { name: 'ai-agents-knowledge-base' },
            icon: 'bvi-book',
            permissions: 'read-ai-knowledge-base',
          },
          // TODO: remove old AI agent report version
          {
            title: 'ai.nav.ai_agent_report',
            route: { name: 'ai-agent-report-old' },
            badge: { text: 'Old', color: 'gray-500' },
            icon: 'bvi-document-text',
            permissions: 'read-voice-agent-report',
            visible: import.meta.env.MODE !== 'production',
          },
          {
            title: 'ai.nav.ai_agent_report',
            route: { name: 'ai-agent-report' },
            icon: 'bvi-document-text',
            permissions: 'read-voice-agent-report',
          },
        ],
      },
      {
        title: 'ai.nav.credit_history',
        route: { name: 'ai-credit-history' },
        icon: 'bvi-history',
        permissions: 'read-ai-credit-history',
      },
    ],
  },
  children: [
    {
      path: 'dashboard',
      name: 'ai-dashboard',
      component: () => import('@/components/ai/dashbord/AiDashboard.vue'),
      meta: {
        permissions: 'read-ai-dashboard',
        breadcrumbs: aiBreadcrumbs({
          label: 'ai.nav.dashboard',
          route: { name: 'ai-dashboard' },
        }),
      },
    },
    {
      path: 'report',
      name: 'ai-report',
      component: () => import('@/components/ai/report/AiReport.vue'),
      meta: {
        permissions: 'read-ai-analysis-report',
        breadcrumbs: aiBreadcrumbs({
          label: 'ai.nav.analysis_report',
          route: { name: 'ai-report' },
        }),
      },
      beforeEnter: loadData,
    },
    {
      path: 'agents/performance',
      name: 'ai-performance-report',
      component: () => import('@/components/ai/agent/performance/AgentPerformance.vue'),
      meta: {
        permissions: 'read-ai-performance-report',
        breadcrumbs: aiBreadcrumbs({
          label: 'ai.nav.agent_performance',
          route: { name: 'ai-performance-report' },
        }),
      },
    },
    {
      path: 'agents/voices',
      name: 'ai-voices-list',
      component: () => import('@/components/ai/agent/voices/VoicesList.vue'),
      meta: {
        permissions: 'read-ai-voices',
        breadcrumbs: aiBreadcrumbs({
          label: 'ai.nav.voices',
          route: { name: 'ai-voices-list' },
        }),
      },
    },
    {
      path: 'agents/credit-history',
      name: 'ai-credit-history',
      component: () => import('@/components/ai/agent/Credit/CreditHistory.vue'),
      meta: {
        permissions: 'read-ai-credit-history',
        breadcrumbs: aiBreadcrumbs({
          label: 'ai.nav.credit_history',
          route: { name: 'ai-credit-history' },
        }),
      },
    },
    {
      path: 'agents/report',
      name: 'ai-agent-report',
      component: () => import('@/components/ai/agent/report/AiAgentReport.vue'),
      meta: {
        permissions: 'read-voice-agent-report',
        breadcrumbs: aiBreadcrumbs({
          label: 'ai.nav.ai_agent_report',
          route: { name: 'ai-agent-report' },
        }),
      },
    },
    // TODO: remove old AI agent report version
    {
      path: 'agents/report/old',
      name: 'ai-agent-report-old',
      component: () => import('@/components/ai/agent/report/AiAgentReport.vue'),
      props: {
        version: 'old',
      },
      meta: {
        permissions: 'read-voice-agent-report',
        canAccess: import.meta.env.MODE !== 'production',
        breadcrumbs: aiBreadcrumbs({
          label: 'ai.nav.ai_agent_report',
          route: { name: 'ai-agent-report' },
        }),
      },
    },
    {
      path: 'settings',
      name: 'ai-settings',
      component: () => import('@/components/ai/settings/AiSettings.vue'),
      meta: {
        permissions: 'read-ai-analysis-settings',
        breadcrumbs: aiBreadcrumbs({
          label: 'ai.nav.analysis_settings',
          route: { name: 'ai-settings' },
        }),
      },
    },
  ],
};

const aiRoutes = aiRoutesGenerator(voipAiRoutes);

export default aiRoutes;
